import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, Plus, Settings, CheckCircle, XCircle, Clock, AlertTriangle,
  Globe, Key, MessageSquare, Upload, Bot, Smartphone, RefreshCw, Edit,
  DollarSign, Users, BarChart3, Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const statusConfig = {
  active: { label: 'Actif', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  suspended: { label: 'Suspendu', color: 'bg-red-100 text-red-700', icon: XCircle },
  inactive: { label: 'Inactif', color: 'bg-gray-100 text-gray-700', icon: AlertTriangle }
};

// Region options
const regions = [
  { value: 'eu', label: 'Europe (EU)', flag: '🇪🇺' },
  { value: 'us', label: 'États-Unis (US)', flag: '🇺🇸' },
  { value: 'asia', label: 'Asie-Pacifique', flag: '🌏' }
];

export default function RCSAgents({ embedded = false }) {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [formData, setFormData] = useState({
    agent_name: '',
    agent_id: '',
    brand_id: '',
    region: 'eu',
    google_project_id: '',
    mode: 'non_conversational',
    price_text_message: 0.01,
    price_rich_message: 0.02,
    price_rich_media: 0.04,
    price_conversation: 0.05,
    conversation_window_hours: 24
  });
  const [credentialsFile, setCredentialsFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchAgents = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/v2/rcs/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAgents(data);
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchAgents();
    if (user?.role === 'superadmin') {
      fetchClients();
    }
  }, [fetchAgents, fetchClients, user]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const json = JSON.parse(event.target.result);
          setCredentialsFile(json);
          setFormData(prev => ({
            ...prev,
            google_project_id: json.project_id || prev.google_project_id
          }));
          toast.success('Fichier de credentials chargé');
        } catch (err) {
          toast.error('Fichier JSON invalide');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    
    if (!selectedClient) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        service_account_json: credentialsFile
      };

      const response = await fetch(`${API_URL}/api/v2/rcs/agents?client_id=${selectedClient}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Agent RCS créé avec succès');
        setCreateDialogOpen(false);
        fetchAgents();
        resetForm();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur lors de la création');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      agent_name: '',
      agent_id: '',
      brand_id: '',
      region: 'eu',
      google_project_id: '',
      mode: 'non_conversational',
      price_text_message: 0.01,
      price_rich_message: 0.02,
      price_rich_media: 0.04,
      price_conversation: 0.05,
      conversation_window_hours: 24
    });
    setCredentialsFile(null);
    setSelectedClient('');
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.inactive;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || client?.company_name || 'Client inconnu';
  };

  if (user?.role !== 'superadmin') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2">Accès restreint</h2>
              <p className="text-gray-500">
                Cette page est réservée au SuperAdmin pour la gestion des agents RCS.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const content = (
    <div className="space-y-6" data-testid="rcs-agents-page">
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Bot className="w-7 h-7 text-indigo-600" />
              Agents RCS
            </h1>
            <p className="text-gray-600 mt-1">
              Gérez les agents RCS pour vos clients
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchAgents}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvel Agent
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Bot className="w-5 h-5 text-indigo-600" />
                    Créer un nouvel Agent RCS
                  </DialogTitle>
                  <DialogDescription>
                    Configurez un agent RCS pour permettre à un client d'envoyer des messages.
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleCreateAgent} className="space-y-6 mt-4">
                  {/* Client Selection */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Client *
                    </Label>
                    <Select value={selectedClient} onValueChange={setSelectedClient}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name} - {client.company_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Agent Name */}
                    <div className="space-y-2">
                      <Label>Nom de l'agent *</Label>
                      <Input
                        value={formData.agent_name}
                        onChange={(e) => setFormData({...formData, agent_name: e.target.value})}
                        placeholder="Mon Agent RCS"
                        required
                      />
                    </div>
                    
                    {/* Agent ID */}
                    <div className="space-y-2">
                      <Label>ID Agent (Google) *</Label>
                      <Input
                        value={formData.agent_id}
                        onChange={(e) => setFormData({...formData, agent_id: e.target.value})}
                        placeholder="my-rcs-agent"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Brand ID */}
                    <div className="space-y-2">
                      <Label>Brand ID (Google)</Label>
                      <Input
                        value={formData.brand_id}
                        onChange={(e) => setFormData({...formData, brand_id: e.target.value})}
                        placeholder="brand-id"
                      />
                    </div>
                    
                    {/* Region */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Région
                      </Label>
                      <Select 
                        value={formData.region} 
                        onValueChange={(v) => setFormData({...formData, region: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {regions.map(r => (
                            <SelectItem key={r.value} value={r.value}>
                              {r.flag} {r.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Google Credentials */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Credentials Google Cloud (JSON)
                    </Label>
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-indigo-400 transition-colors">
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="credentials-upload"
                      />
                      <label htmlFor="credentials-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">
                          {credentialsFile ? (
                            <span className="text-green-600 font-medium">
                              ✓ Fichier chargé ({formData.google_project_id})
                            </span>
                          ) : (
                            'Cliquez pour uploader le fichier service account'
                          )}
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Billing Mode */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Mode de facturation
                    </Label>
                    <Select 
                      value={formData.mode} 
                      onValueChange={(v) => setFormData({...formData, mode: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="non_conversational">Non-conversationnel (par message)</SelectItem>
                        <SelectItem value="conversational">Conversationnel (par session)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Pricing */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Tarification (EUR)
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Message texte</Label>
                        <Input
                          type="number"
                          step="0.001"
                          value={formData.price_text_message}
                          onChange={(e) => setFormData({...formData, price_text_message: parseFloat(e.target.value)})}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Message rich</Label>
                        <Input
                          type="number"
                          step="0.001"
                          value={formData.price_rich_message}
                          onChange={(e) => setFormData({...formData, price_rich_message: parseFloat(e.target.value)})}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Rich media</Label>
                        <Input
                          type="number"
                          step="0.001"
                          value={formData.price_rich_media}
                          onChange={(e) => setFormData({...formData, price_rich_media: parseFloat(e.target.value)})}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Conversation</Label>
                        <Input
                          type="number"
                          step="0.001"
                          value={formData.price_conversation}
                          onChange={(e) => setFormData({...formData, price_conversation: parseFloat(e.target.value)})}
                        />
                      </div>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                      Annuler
                    </Button>
                    <Button type="submit" disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700">
                      {submitting ? 'Création...' : 'Créer l\'agent'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Agents</p>
                  <p className="text-2xl font-bold">{agents.length}</p>
                </div>
                <div className="p-3 bg-indigo-100 rounded-lg">
                  <Bot className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Agents Actifs</p>
                  <p className="text-2xl font-bold text-green-600">
                    {agents.filter(a => a.status === 'active').length}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">En attente</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {agents.filter(a => a.status === 'pending').length}
                  </p>
                </div>
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Clients couverts</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {new Set(agents.map(a => a.client_id)).size}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Agents List */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des Agents</CardTitle>
            <CardDescription>
              Tous les agents RCS configurés pour vos clients
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto" />
                <p className="mt-4 text-gray-500">Chargement...</p>
              </div>
            ) : agents.length === 0 ? (
              <div className="text-center py-12">
                <Bot className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun agent configuré</h3>
                <p className="text-gray-500 mb-4">
                  Créez votre premier agent RCS pour permettre à vos clients d'envoyer des messages.
                </p>
                <Button onClick={() => setCreateDialogOpen(true)} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un agent
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="agents-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Agent</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Client</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Région</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Mode</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Statut</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((agent) => {
                      const region = regions.find(r => r.value === agent.region) || regions[0];
                      return (
                        <tr key={agent.id} className="border-b hover:bg-gray-50">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                                <Smartphone className="w-5 h-5 text-indigo-600" />
                              </div>
                              <div>
                                <p className="font-medium">{agent.agent_name}</p>
                                <p className="text-xs text-gray-500">{agent.agent_id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-gray-400" />
                              {getClientName(agent.client_id)}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className="flex items-center gap-1">
                              {region.flag} {region.label}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <Badge variant="outline">
                              {agent.mode === 'conversational' ? 'Conversationnel' : 'Par message'}
                            </Badge>
                          </td>
                          <td className="py-4 px-4">
                            {getStatusBadge(agent.status || 'pending')}
                          </td>
                          <td className="py-4 px-4 text-center">
                            <div className="flex justify-center gap-1">
                              <Button variant="ghost" size="sm">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white rounded-lg shadow-sm">
                <MessageSquare className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-indigo-900 mb-1">Configuration Google RCS</h3>
                <p className="text-sm text-indigo-700 mb-3">
                  Pour créer un agent RCS, vous devez d'abord configurer un projet Google Cloud avec l'API RCS Business Messaging activée.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="bg-white">
                    <Globe className="w-4 h-4 mr-2" />
                    Documentation Google
                  </Button>
                  <Button variant="outline" size="sm" className="bg-white">
                    <Key className="w-4 h-4 mr-2" />
                    Guide de configuration
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
  );

  if (embedded) {
    return content;
  }

  return <Layout>{content}</Layout>;
}
